/**
 * lib/transition.ts — the arithmetic of one track going into the next: where the overlap starts,
 * how long it is, where the next track starts from, and how far into it each moment is — since
 * task 56 the crossfader's way, the gains being the mixer's.
 *
 * Task 45. `spec.md` «Микс и дополнения плеера», «Переход». Pure: numbers in, numbers out.
 * `engine/player.ts` reads the playing deck's second, asks {@link frameAt}, and — since task 56 —
 * moves the pult's crossfader by what it says; the gains are the mixer's (`lib/pult-mixer.ts`),
 * by this module's {@link equalPower}. `test/transition.test.ts` pins every rule here on numbers.
 *
 * ## Where the overlap starts
 *
 * At the last beats of the music, not in the silence at the end of the file (story 103): the
 * playing track's `durationS − tailS − lengthS`. The next track starts at the end of its own
 * silence, `leadInS` (story 104). Both silences are the server's measure (task 46); without one,
 * 0. The overlap is {@link BLEND_S}, but never more than half the music of either track: a track
 * of ten seconds is not all overlap, and the next one must not end before the overlap does.
 *
 * ## Equal power
 *
 * The old deck goes down by the cosine, the new one comes up by the sine, of the overlap's share
 * times a quarter turn. Two unrelated sounds add by power, not by level, and cos² + sin² = 1: the
 * middle of the overlap is as loud as either end. Two straight lines would meet at a half each —
 * a quarter of the power, a dip of 3 dB right in the middle (story 113 asks for none).
 *
 * ## The overlap follows the old track's own second
 *
 * {@link frameAt} takes the playing deck's position, not a clock. A pause freezes both decks and
 * so the overlap with them; a deck tempo of +8 % shortens it as it shortens the music; a late
 * start — the player looks every so often — only skips the first sliver of the curve, and the
 * overlap still ends where the music ends.
 *
 * ## The mix (task 47)
 *
 * In a blend both decks play at the deck's tempo, as the motor turns them. In `mix`, with both
 * tracks measured surely, `lib/mix.ts` brings the next one to the playing one's tempo: the overlap
 * is sixteen beats ({@link TransitionPlan.mix} says at what speed and on what beats), and starts
 * on the beat that lays the next track's first beat on a beat of the playing one — never later
 * than the blend's start would be. Tempos too far apart make a short blend of
 * {@link SHORT_BLEND_S}; a measure missing or unsure, the blend of {@link BLEND_S} (story 114).
 */

import type { TrackBeat, TransitionMode } from './api';
import { SHORT_BLEND_S, alignedStart, fitMix, mixLengthS } from './mix';
import type { MixFit } from './mix';

/** The overlap of a blend, in seconds of the playing track (`spec.md`: «8 секунд для blend»). */
export const BLEND_S = 8;

/** At this share of the overlap the new track becomes the one the screen shows (story 106). */
export const SWITCH_AT = 0.5;

/** The longest share of a track's music the overlap may take. */
const MAX_SHARE = 0.5;

/** What the overlap is made from: the two tracks' durations and the server's silences. */
export interface TransitionInput {
  /** The playing track's length, as the element knows it. */
  readonly durationS: number;
  /** Silence at the end of the playing track; absent until measured. */
  readonly tailS?: number | undefined;
  /** Silence at the start of the next track; absent until measured. */
  readonly nextLeadInS?: number | undefined;
  /** The next track's length; absent or 0 — unknown, and then it does not limit the overlap. */
  readonly nextDurationS?: number | undefined;
  readonly nextTailS?: number | undefined;
  /** Task 47, `mix` only: silence at the start of the playing track, for its first beat. */
  readonly leadInS?: number | undefined;
  /** Task 47, `mix` only: the two tracks' beats as the server measured them. */
  readonly beat?: TrackBeat | null | undefined;
  readonly nextBeat?: TrackBeat | null | undefined;
  /** Task 47, `mix` only: the playing deck's speed as a share of the deck's tempo; 1 when absent. */
  readonly factor?: number | undefined;
}

export interface TransitionPlan {
  /** The overlap, in seconds of the playing track. */
  readonly lengthS: number;
  /** The playing track's second at which the next one starts. */
  readonly startAtS: number;
  /** The next track's second it starts from. */
  readonly nextFromS: number;
  /** Task 47: the overlap is a mix — the next deck's speed and the beats it keeps to. */
  readonly mix?: MixFit;
}

/**
 * One moment of the overlap: how far it is. Task 56: that is the crossfader's way from the old
 * deck's side to the new one's — the two gains are the mixer's, by {@link equalPower}.
 */
export interface TransitionFrame {
  /** 0 before the overlap, 1 at its end and after: the share of the crossfader's way. */
  readonly progress: number;
  /** The new track is the one the screen, the lock screen and «недавние» know. */
  readonly switched: boolean;
  /** The old deck is silent: pause it, and the overlap is over. */
  readonly done: boolean;
}

/** A measure the server gave, or 0: never negative, never `NaN`. */
function seconds(value: number | undefined): number {
  return value !== undefined && Number.isFinite(value) && value > 0 ? value : 0;
}

/** The music of a track: its length without the silence at its end, and at its start. */
function music(durationS: number, tailS: number | undefined, leadInS = 0): number {
  return seconds(durationS) - seconds(tailS) - leadInS;
}

/**
 * The overlap for `mode`, or `undefined` when there is none — `cut`, an unknown duration, a track
 * that is all silence. Then the next track starts when this one ends, as before task 45.
 */
export function planTransition(
  mode: TransitionMode,
  input: TransitionInput,
): TransitionPlan | undefined {
  if (mode === 'cut') return undefined;
  const ours = music(input.durationS, input.tailS);
  if (!(ours > 0)) return undefined;
  const nextFromS = seconds(input.nextLeadInS);
  const theirs =
    seconds(input.nextDurationS) > 0
      ? music(seconds(input.nextDurationS), input.nextTailS, nextFromS)
      : Number.POSITIVE_INFINITY;
  const factor = input.factor ?? 1;
  const fit =
    mode === 'mix'
      ? fitMix(
          { beat: input.beat, leadInS: input.leadInS },
          { beat: input.nextBeat, leadInS: nextFromS },
          factor,
        )
      : undefined;
  const wantS =
    fit === undefined
      ? BLEND_S
      : fit === 'apart'
        ? SHORT_BLEND_S
        : mixLengthS(60 / fit.grid.periodS);
  const lengthS = Math.min(wantS, ours * MAX_SHARE, Math.max(0, theirs) * MAX_SHARE);
  if (!(lengthS > 0)) return undefined;
  if (fit === undefined || fit === 'apart') return { lengthS, startAtS: ours - lengthS, nextFromS };
  const startAtS = alignedStart(fit.grid, ours - lengthS, nextFromS, factor, fit.factor);
  return { lengthS, startAtS, nextFromS, mix: { factor: fit.factor, grid: fit.grid } };
}

/** The two gains at `progress` of the overlap — see «Equal power». */
export function equalPower(progress: number): { readonly out: number; readonly in: number } {
  const share = Math.min(1, Math.max(0, progress));
  if (share === 0) return { out: 1, in: 0 };
  if (share === 1) return { out: Math.cos(Math.PI / 2), in: 1 };
  return { out: Math.cos((share * Math.PI) / 2), in: Math.sin((share * Math.PI) / 2) };
}

/** The overlap when the playing track is at `positionS` — see «The overlap follows…». */
export function frameAt(plan: TransitionPlan, positionS: number): TransitionFrame {
  const raw = (positionS - plan.startAtS) / plan.lengthS;
  const progress = Number.isFinite(raw) ? Math.min(1, Math.max(0, raw)) : 0;
  return {
    progress,
    switched: progress >= SWITCH_AT,
    done: progress >= 1,
  };
}

/**
 * Task 56: an overlap starting now, at `positionS` of the playing track — AUTO off, «следующий»
 * pressed (`engine/player.ts`, `oneShot`). The next track starts from `nextLeadInS` — where its deck
 * stands. A blend of {@link BLEND_S}; in `mix`, with both tracks measured surely, the mix's own
 * length on its tempo, started on the last beat at or before now that lays the next track's first
 * beat on a beat of this one (the player starts it late by as much as it is late). `cut` too is a
 * blend: a button pressed for a transition gets one.
 */
export function planNow(
  mode: TransitionMode,
  input: TransitionInput & { readonly positionS: number },
): TransitionPlan {
  const nextFromS = seconds(input.nextLeadInS);
  const positionS = Math.max(0, input.positionS);
  if (mode === 'mix') {
    const factor = input.factor ?? 1;
    const fit = fitMix(
      { beat: input.beat, leadInS: input.leadInS },
      { beat: input.nextBeat, leadInS: nextFromS },
      factor,
    );
    if (fit === 'apart') return { lengthS: SHORT_BLEND_S, startAtS: positionS, nextFromS };
    if (fit !== undefined) {
      const lengthS = mixLengthS(60 / fit.grid.periodS);
      const startAtS = alignedStart(fit.grid, positionS, nextFromS, factor, fit.factor);
      return { lengthS, startAtS, nextFromS, mix: { factor: fit.factor, grid: fit.grid } };
    }
  }
  return { lengthS: BLEND_S, startAtS: positionS, nextFromS };
}
